<template>
	<div
		class="g-block-pay-video-player"
		:class="{
			'g-block-pay-video-player--edit': editable,
			'is-active': editable && active,
			'g-block-pay-video-player--h5': h5,
			'g-block-pay-video-player--unlocked': isUnlocked,
			'is-playing': isPlaying,
		}"
		:style="{ width: blockStyle.playerWidth }"
		@click="onWrapClick"
	>
		<div class="g-block-pay-video-player__card">
			<div
				class="g-block-pay-video-player__shell"
				:style="shellStyle"
			>
				<img
					v-if="coverUrl && !hasPlayed"
					class="g-block-pay-video-player__cover"
					:src="coverUrl"
					alt=""
				/>
				<video
					v-if="videoUrl"
					ref="videoEl"
					class="g-block-pay-video-player__video"
					:class="{ 'is-visible': hasPlayed }"
					:src="videoUrl"
					:controls="hasPlayed"
					playsinline
					webkit-playsinline
					preload="metadata"
					@loadedmetadata="onLoadedMetadata"
					@timeupdate="onTimeUpdate"
					@ended="onEnded"
					@pause="onPause"
					@play="onPlay"
				></video>
				<div
					v-if="!hasPlayed"
					class="g-block-pay-video-player__content"
				>
					<span class="g-block-pay-video-player__duration">{{ durationText }}</span>

					<div class="g-block-pay-video-player__controls">
						<button
							type="button"
							class="g-block-pay-video-player__play"
							:aria-label="isPlaying ? '暂停' : '播放'"
							@click.stop="togglePlay"
						>
							<span v-if="isPlaying" class="g-block-pay-video-player__pause"></span>
							<span v-else class="g-block-pay-video-player__triangle"></span>
						</button>
					</div>
				</div>
			</div>

			<p class="g-block-pay-video-player__title">{{ playerData.title || '暮色下的湖畔灯火' }}</p>

			<PayMediaPaySection
				class="g-block-pay-video-player__pay"
				:currency="playerData.currency"
				:price="playerData.price"
				:button-text="playerData.buttonText"
				:button-bg="playerData.buttonBg"
				:show-info-bar="showPayInfoBar"
				:info-text="previewInfoText"
				:is-unlocked="isUnlocked"
				:h5="h5"
				@unlock="onUnlockClick"
			/>
		</div>
	</div>
</template>

<script>
	import baseFunc from '../../../../config/base_func.js';
	import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
	import PayMediaPaySection from '../../../components/PayMediaPaySection.vue';
	import { formatPayPreviewDuration } from '../../../utils/payMediaCommon.js';
	import {
		getPayVideoPlayerBlockStyle,
		getPayVideoPlayerCoverUrl,
		getPayVideoPlayerPreviewHint,
		getPayVideoPlayerVideoUrl,
		isPayVideoPlayerUnlocked,
		normalizePayVideoPlayerData,
		setPayVideoPlayerUnlocked,
	} from '../../../utils/payVideoPlayer.js';

	export default {
		name: 'GPayVideoPlayerShell',
		mixins: [geditPayUnlockMixin],
		components: { PayMediaPaySection },
		props: {
			data: {
				type: Object,
				required: true,
			},
			blockId: {
				type: String,
				default: '',
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
			compact: {
				type: Boolean,
				default: false,
			},
			idKey: {
				type: String,
				default: '',
			},
		},
		emits: ['select'],
		data() {
			return {
				unlockedLocal: false,
				isPlaying: false,
				hasPlayed: false,
				currentTime: 0,
				duration: 0,
				permissionedVideoUrl: '',
				mediaPlayLimit: null,
				hasRequestedPermission: false,
			};
		},
		computed: {
			playerData() {
				return normalizePayVideoPlayerData(this.data);
			},
			blockStyle() {
				return getPayVideoPlayerBlockStyle(this.playerData, { h5: this.h5 });
			},
			coverUrl() {
				return getPayVideoPlayerCoverUrl(this.playerData);
			},
			videoUrl() {
				return this.permissionedVideoUrl || getPayVideoPlayerVideoUrl(this.playerData);
			},
			shellStyle() {
				return {
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			durationText() {
				if (this.duration) {
					return this.playerData.video?.durationText || this.formatDuration(this.duration);
				}
				return this.playerData.video?.durationText || '00:00';
			},
			isUnlocked() {
				if (this.editable) return false;
				return this.unlockedLocal || isPayVideoPlayerUnlocked(this.blockId) || this.isGeditModulePaid;
			},
			isInteractive() {
				return this.editable || this.h5;
			},
			shouldLimitPlayback() {
				return !this.isUnlocked && this.h5 && !this.editable;
			},
			previewLimit() {
				return this.playerData.freePreviewDuration;
			},
			showPayInfoBar() {
				if (this.isUnlocked) return false;
				const total = this.playerData.video?.duration || 0;
				return this.previewLimit > 0 && total > 0 && this.previewLimit < total;
			},
			previewInfoText() {
				const duration = formatPayPreviewDuration(this.previewLimit);
				const hint = getPayVideoPlayerPreviewHint(this.playerData);
				return hint.replace(duration, `<strong>${duration}</strong>`);
			},
		},
		watch: {
			blockId: {
				immediate: true,
				handler() {
					this.syncUnlockState();
				},
			},
			data: {
				handler() {
					this.resetPlayer();
				},
				deep: true,
			},
		},
		mounted() {
			this.duration = this.playerData.video?.duration || 0;
		},
		beforeUnmount() {
			this.pauseVideo();
		},
		methods: {
			syncUnlockState() {
				this.unlockedLocal = isPayVideoPlayerUnlocked(this.blockId);
			},
			formatDuration(seconds) {
				const mins = Math.floor(seconds / 60);
				const secs = Math.floor(seconds % 60);
				return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
			},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
			onUnlockClick() {
				this.runGeditUnlockFlow({
					applyUnlock: () => {
						setPayVideoPlayerUnlocked(this.blockId, true);
						this.unlockedLocal = true;
					},
				});
			},
			resetPlayer() {
				this.pauseVideo();
				this.currentTime = 0;
				this.duration = this.playerData.video?.duration || 0;
				this.hasPlayed = false;
			},
			onLoadedMetadata() {
				const video = this.$refs.videoEl;
				if (video && Number.isFinite(video.duration)) {
					this.duration = Math.floor(video.duration);
				}
			},
			onTimeUpdate() {
				const video = this.$refs.videoEl;
				if (!video) return;
				if (this.shouldLimitPlayback && video.currentTime > this.previewLimit) {
					video.pause();
					video.currentTime = this.previewLimit;
				}
				this.currentTime = video.currentTime || 0;
			},
			onPlay() {
				this.isPlaying = true;
				this.hasPlayed = true;
			},
			onPause() {
				this.isPlaying = false;
			},
			onEnded() {
				this.isPlaying = false;
				this.currentTime = 0;
				const video = this.$refs.videoEl;
				if (video) {
					video.currentTime = 0;
				}
			},
			togglePlay() {
				if (!this.isInteractive) return;
				const video = this.$refs.videoEl;
				if (!video) return;
				if (this.isPlaying) {
					video.pause();
				} else {
					if (this.h5 && this.idKey && this.playerData.video?.id && !this.hasRequestedPermission) {
						this.hasRequestedPermission = true;
						this.requestPlayPermission().then(() => {
							video.play();
						}).catch(err => {
							this.$message.error(err.data?.message || '请求失败');
							video.pause();
							this.hasRequestedPermission = false;
						});
					} else {
						video.play();
					}
				}
			},
			//访问路径中取指定的值
			getQueryStringByChannel(name) {
				const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
				if (!window.location.href.split('?')[1]) {
					return null
				}
				const re = window.location.href.split('?')[1].match(reg)
				if (re != null) return unescape(re[2])
				return null
			},
			requestPlayPermission() {
				return this.$apiRequest.post('/api/getMediaPlayPermissions', {
					attach_id: this.playerData.video.id,
					id_key: this.idKey,
					unique: '',
					app_type: 1,
					p_key: this.getQueryStringByChannel('p_key')==null?'':this.getQueryStringByChannel('p_key'),
				}).then(res => {
					if (res.data?.play_url) {
						this.permissionedVideoUrl = baseFunc.getQiniusign(res.data.play_url);
					}
					if (res.data?.media_play_limit !== undefined) {
						this.mediaPlayLimit = res.data.media_play_limit;
					}
				})
			},
			pauseVideo() {
				const video = this.$refs.videoEl;
				if (video) {
					video.pause();
				}
				this.isPlaying = false;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-pay-video-player {
		box-sizing: border-box;
		max-width: 100%;
		margin: 0 auto;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-pay-video-player__card {
		background: #fff;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-video-player__title {
		margin: 0;
		padding: 12px 14px;
		font-size: 15px;
		font-weight: 600;
		line-height: 1.4;
		color: #1f1f1f;
	}

	.g-block-pay-video-player__shell {
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 9;
		overflow: hidden;
		background: #1a1a2e;
	}

	.g-block-pay-video-player__cover {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.g-block-pay-video-player__video {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
		pointer-events: none;

		&.is-visible {
			opacity: 1;
			pointer-events: auto;
			z-index: 2;
		}
	}

	.g-block-pay-video-player__content {
		position: absolute;
		inset: 0;
		z-index: 3;
		pointer-events: none;
	}

	.g-block-pay-video-player__duration {
		position: absolute;
		right: 16px;
		bottom: 16px;
		padding: 2px 8px;
		border-radius: 4px;
		background: rgba(0, 0, 0, 0.55);
		color: #fff;
		font-size: 11px;
		line-height: 1.4;
	}

	.g-block-pay-video-player__controls {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		display: flex;
		align-items: center;
		justify-content: center;
		pointer-events: auto;
	}

	.g-block-pay-video-player__play {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 48px;
		border: none;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.45);
		color: #fff;
		line-height: 1;
		outline: none;
		cursor: pointer;
		.g-block-pay-video-player--edit & {
			cursor: pointer;
		}
	}

	.g-block-pay-video-player__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 4px;
		border-top: 10px solid transparent;
		border-bottom: 10px solid transparent;
		border-left: 16px solid currentColor;
	}

	.g-block-pay-video-player__pause {
		display: block;
		width: 14px;
		height: 16px;
		border-left: 4px solid currentColor;
		border-right: 4px solid currentColor;
		box-sizing: border-box;
	}

	.g-block-pay-video-player--h5 {
		.g-block-pay-video-player__card {
			border-radius: 0.24rem;
			box-shadow: 0 0 0.24rem rgba(0, 0, 0, 0.1);
		}

		.g-block-pay-video-player__title {
			padding: 0.24rem 0.28rem;
			font-size: 0.32rem;
		}
		.g-pay-media-pay{
			padding: 0 0.28rem 0.28rem;
			border-radius: 0.24rem;
		}
		.g-block-pay-video-player__duration {
			right: 0.32rem;
			bottom: 0.32rem;
			padding: 0.04rem 0.16rem;
			border-radius: 0.08rem;
			font-size: 0.22rem;
		}

		.g-block-pay-video-player__play {
			width: 0.96rem;
			height: 0.96rem;
			cursor: pointer;
		}

		.g-block-pay-video-player__triangle {
			margin-left: 0.08rem;
			border-top-width: 0.2rem;
			border-bottom-width: 0.2rem;
			border-left-width: 0.32rem;
		}

		.g-block-pay-video-player__pause {
			width: 0.28rem;
			height: 0.32rem;
			border-left-width: 0.08rem;
			border-right-width: 0.08rem;
		}

		.g-block-pay-video-player__pay {
			:deep(.g-pay-media-pay) {
				padding: 0.28rem;
				border-radius: 0.24rem;
			}

			:deep(.g-pay-media-pay__info) {
				gap: 0.16rem;
				padding: 0.2rem 0.24rem;
				border-radius: 0.2rem;
				font-size: 0.24rem;
			}
			:deep(.g-pay-media-pay__main) {
				margin-top: 0.24rem;
			}
			:deep(.g-pay-media-pay__info-icon) {
				font-size: 0.28rem;
			}

			:deep(.g-pay-media-pay__currency) {
				font-size: 0.28rem;
			}

			:deep(.g-pay-media-pay__price-value) {
				font-size: 0.56rem;
			}

			:deep(.g-pay-media-pay__unlock-btn) {
				padding: 0.2rem 0.32rem;
				font-size: 0.26rem;
			}
		}
	}
</style>
